const UNKNOWN_DATE_KEY = 'unknown'

// Кэш результата getDeliveries(). Сбрасывается через invalidateDeliveries()
// каждый раз, когда изменяется количество товара в хранилище.
let _deliveriesCache = null

const invalidateDeliveries = () => {
  _deliveriesCache = null
}

const formatDateKey = (isoDate) => {
  if (!isoDate) return UNKNOWN_DATE_KEY
  const [year, month, day] = isoDate.split('-')
  if (!year || !month || !day) return UNKNOWN_DATE_KEY
  return `${year}${month}${day}`
}

const formatDateLabel = (isoDate) => {
  if (!isoDate) return ''
  const [, month, day] = isoDate.split('-')
  if (!month || !day) return ''
  return `${day}.${month}`
}

const createDeliveriesService = ({
  store,
  routing,
  routes,
  ProductModel,
  imagesPath = '/uploads/images/original/',
  stubImage = '/images/technosuccess-placeholder.svg',
}) => {

  const getDeliveries = () => {
    if (_deliveriesCache !== null) return _deliveriesCache

    const deliveries = {}
    routing.setRoutingData(routes)

    const deliveriesByCart = (store.deliveriesByCart && typeof store.deliveriesByCart === 'object')
      ? store.deliveriesByCart
      : {}

    store.productCollection.getAll().forEach(cart => {
      const product = cart.product
      if (typeof product.warehouseStock === 'undefined') {
        console.log('Product stocks unavailable: ' + product.productCode)
        return
      }

      const entry = deliveriesByCart[product.id] && typeof deliveriesByCart[product.id] === 'object'
        ? deliveriesByCart[product.id]
        : null
      const slices = entry && Array.isArray(entry.slices) ? entry.slices : []
      const totalMaxAmount = entry && typeof entry.totalAmount === 'number' ? entry.totalAmount : 0

      // amount === 0 — позиция только что удалена локально (skipApi-эмит после
      // CART_DELETE_PRODUCTS из OOS-блока). Не показываем нигде, ждём ререндера.
      if (cart.amount === 0) {
        return
      }

      // price === 0 — товар недоступен для заказа, уходит в OOS-блок.
      // Из блоков доставки исключаем.
      const cartPrice = typeof cart.price === 'number' ? cart.price : parseFloat(cart.price)
      if (cartPrice === 0) {
        return
      }

      // Out-of-stock: сервер не вернул запись доставки ИЛИ вернул totalAmount=0.
      // Зеркалит условие getOutOfStockProducts — иначе товары без записи
      // в deliveriesByCart продублировались бы (UNKNOWN_DATE в основном
      // списке + OOS-блок). Маркер удаления (amount < 0) не трогаем — нужен
      // для «восстановить» в модалке.
      if ((!entry || totalMaxAmount === 0) && cart.amount > 0) {
        return
      }

      const productImages = Array.isArray(product.images) ? product.images : []
      const images = [...productImages].sort((a, b) => a.position - b.position)
      const imagePath = images.length ? imagesPath + images[0].path : stubImage
      // imageId серверным записям проставляет loadProductsToStore из
      // metadata.image_ids; у клиентски добавленных (живой поиск) его нет —
      // берём id первой картинки payload'а, чтобы лоадер сразу грузил свою.
      const productImageId = product.imageId ?? (images.length && images[0].id != null ? images[0].id : undefined)
      const productName = (Array.isArray(product.descriptions) && product.descriptions[0] && product.descriptions[0].name)
        ? product.descriptions[0].name
        : (product.name || '')

      const pushModel = (key, amount, sliceMaxAmount, isOrder = false) => {
        // Слайс доставки с нулевым количеством не отображаем — товар пропадает
        // из этой группы доставки. Маркер удаления (amount < 0) при этом
        // сохраняется: скрытая строка нужна для «восстановить» в модалке удаления.
        if (amount === 0) {
          return
        }
        if (!deliveries[key]) {
          deliveries[key] = []
        }
        const cartPriceType = cart.extra && cart.extra.price_type ? cart.extra.price_type : ''
        const model = new ProductModel(
          product.id,
          product.productCode,
          amount,
          imagePath,
          productImageId,
          product.quantityStep,
          product.warehouseStock,
          product.hasFavorites,
          productName,
          cart.price,
          cart.bestPrice,
          // У локально добавленной позиции (addProductToStore) oldPrice нет —
          // без фолбэка в главном ценовом слоте строки рисуется 0 ₽ (guard
          // formatNumber) рядом с корректным итогом. oldPrice=price — штатный
          // вид позиции без скидки; серверные записи всегда несут свой oldPrice.
          cart.oldPrice ?? cart.price,
          true,
          cart.hasOwnProperty('isActive') ? cart.isActive : true,
          product.weight,
          product.length,
          product.width,
          product.height,
          routing.generate('product_show', { id: product.id }),
          cartPriceType,
          isOrder,
        )
        model.sliceMaxAmount = sliceMaxAmount
        model.totalMaxAmount = totalMaxAmount
        deliveries[key].push(model)
      }

      if (slices.length === 0) {
        pushModel(UNKNOWN_DATE_KEY, cart.amount, cart.amount)
        return
      }

      const cartAmount = typeof cart.amount === 'number' ? cart.amount : 0
      const slicesTotal = slices.reduce(
        (sum, slice) => sum + (typeof slice.amount === 'number' ? slice.amount : 0), 0)

      // Слайсы доставки — снапшот распределения с сервера на момент загрузки корзины.
      // Пока пользователь не менял общее количество (cart.amount === сумме слайсов),
      // отдаём серверное распределение как есть — оно учитывает приоритеты складов/транзит.
      if (cartAmount >= 0 && cartAmount === slicesTotal) {
        slices.forEach(slice => {
          const key = formatDateKey(slice.date)
          const sliceMaxAmount = typeof slice.warehouseAmount === 'number' ? slice.warehouseAmount : slice.amount
          pushModel(key, slice.amount, sliceMaxAmount, slice.isOrder === true)
        })
        return
      }

      // Количество изменено (или товар помечен на удаление) — перераспределяем
      // живой cart.amount по датам-слайсам: cart.amount остаётся единственным
      // источником истины, слайсы дают только даты и складской потолок.
      const orderedSlices = [...slices].sort((a, b) =>
        formatDateKey(a.date).localeCompare(formatDateKey(b.date)))
      let remaining = cartAmount > 0 ? cartAmount : 0

      orderedSlices.forEach((slice, index) => {
        const key = formatDateKey(slice.date)
        const sliceMaxAmount = typeof slice.warehouseAmount === 'number' ? slice.warehouseAmount : slice.amount

        // Маркер удаления (cart.amount === -1) пробрасываем во все слайсы,
        // чтобы $fillProduct повесил d-none на все группы доставки товара.
        if (cartAmount < 0) {
          pushModel(key, cartAmount, sliceMaxAmount, slice.isOrder === true)
          return
        }

        const isLast = index === orderedSlices.length - 1
        const cap = sliceMaxAmount > 0 ? sliceMaxAmount : remaining
        const sliceAmount = isLast ? remaining : Math.min(remaining, cap)
        remaining -= sliceAmount
        pushModel(key, sliceAmount, sliceMaxAmount, slice.isOrder === true)
      })
    })

    _deliveriesCache = deliveries
    return _deliveriesCache
  }

  return { getDeliveries }
}

export { createDeliveriesService, formatDateKey, formatDateLabel, UNKNOWN_DATE_KEY, invalidateDeliveries }
export default createDeliveriesService
